import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  TrendingUp, TrendingDown, Brain, BarChart3, Target,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

/**
 * Next-Day Direction predictor.
 * Calls /api/stock/next-day-direction/{ticker}?years=N
 * Renders prediction, backtest stats, prediction history, top features.
 */
export default function NextDayDirection({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [years, setYears] = useState("5y");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/stock/next-day-direction/${ticker}?window=${years}`,
          { timeout: 120000 }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to compute prediction");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, years, refreshTick]);

  if (loading) {
    return (
      <div className="rounded border border-gray-800 bg-gray-900/60 p-6 text-center" data-testid="ndd-loading">
        <Brain className="w-8 h-8 text-cyan-400 mx-auto animate-pulse" />
        <div className="mt-2 text-sm text-gray-300">Training {years}-year walk-forward model for {ticker}…</div>
        <div className="text-[11px] text-gray-500 mt-1">~5–15s on first run; cached 6h after.</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 space-y-2" data-testid="ndd-skeleton">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
          <span className="text-[11px] text-amber-300/80">Refreshing next-day direction…</span>
        </div>
        <div className="h-32 bg-slate-800/40 rounded animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const bt = data.backtest || {};
  const isUp = data.prediction === "UP";
  const confColor = data.confidence_pct >= 65 ? "text-cyan-300" : data.confidence_pct >= 55 ? "text-blue-300" : "text-gray-300";
  const verdictColor = bt.edge_vs_base >= 0.05 ? "text-green-400"
    : bt.edge_vs_base >= 0.02 ? "text-blue-300"
    : "text-gray-400";

  const history = bt.history || [];
  const featureImportance = (data.feature_importance || []).slice(0, 12);

  return (
    <div className="space-y-3" data-testid="next-day-direction-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 bg-[#0d0d0d] border border-gray-800 rounded p-3">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-cyan-400" />
          <h3 className="text-base font-semibold text-white">{ticker} · Next-Day Direction</h3>
          <span className="px-2 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px] font-mono">
            {data.model}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">As of {data.as_of} · ${data.last_close}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={years}
            onChange={(e) => setYears(e.target.value)}
            data-testid="ndd-years-select"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs"
          >
            <option value="2d">2 days</option>
            <option value="10d">10 days</option>
            <option value="1mo">1 month</option>
            <option value="3mo">3 months</option>
            <option value="6mo">6 months</option>
            <option value="1y">1 year</option>
            <option value="2y">2 years</option>
            <option value="3y">3 years</option>
            <option value="5y">5 years</option>
            <option value="7y">7 years</option>
            <option value="10y">10 years</option>
          </select>
          <button
            onClick={() => setRefreshTick((v) => v + 1)}
            data-testid="ndd-refresh"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-700 bg-gray-900 hover:bg-gray-800"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Prediction card */}
      <div className={`rounded-lg border p-5 text-center ${
        isUp ? "border-green-700/60 bg-gradient-to-br from-green-950/40 to-emerald-950/20" : "border-red-700/60 bg-gradient-to-br from-red-950/40 to-rose-950/20"
      }`} data-testid="ndd-prediction-card">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Prediction for next trading day</div>
        <div className="mt-2 flex items-center justify-center gap-2">
          {isUp
            ? <TrendingUp className="w-12 h-12 text-green-400" />
            : <TrendingDown className="w-12 h-12 text-red-400" />}
          <div className={`text-4xl font-extrabold font-mono ${isUp ? "text-green-300" : "text-red-300"}`}
               data-testid="ndd-prediction-direction">
            {data.prediction}
          </div>
        </div>
        <div className={`mt-2 text-2xl font-bold font-mono ${confColor}`} data-testid="ndd-confidence">
          {data.confidence_pct}% confidence
        </div>
        <div className="text-[11px] text-gray-500 mt-1 font-mono">
          P(up) = {(data.prob_up * 100).toFixed(2)}% · P(down) = {((1 - data.prob_up) * 100).toFixed(2)}%
        </div>
        <div className={`inline-block mt-3 px-3 py-1 rounded text-xs font-semibold ${verdictColor} bg-gray-900/60 border border-gray-700`}
             data-testid="ndd-verdict">
          {data.verdict}
        </div>
      </div>

      {/* Backtest summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ndd-backtest-grid">
        <Stat
          label={`${windowLabel(years)} Accuracy`}
          value={`${(bt.accuracy * 100).toFixed(2)}%`}
          sub={`95% CI [${(bt.ci_95_low * 100).toFixed(1)}%, ${(bt.ci_95_high * 100).toFixed(1)}%]`}
          icon={<Target className="w-3 h-3" />}
          highlight
          testid="ndd-stat-accuracy"
        />
        <Stat
          label="Base UP Rate"
          value={`${(bt.base_up_rate * 100).toFixed(2)}%`}
          sub="naive baseline"
          testid="ndd-stat-base"
        />
        <Stat
          label="Edge vs Base"
          value={`${bt.edge_vs_base >= 0 ? "+" : ""}${(bt.edge_vs_base * 100).toFixed(2)}pp`}
          color={bt.edge_vs_base >= 0.02 ? "text-green-400" : bt.edge_vs_base >= 0 ? "text-blue-300" : "text-amber-300"}
          icon={<BarChart3 className="w-3 h-3" />}
          testid="ndd-stat-edge"
        />
        <Stat
          label="Backtest Samples"
          value={bt.samples?.toLocaleString?.() || "—"}
          sub={`${data.n_train_rows} rows trained`}
          testid="ndd-stat-samples"
        />
      </div>

      {/* Confusion + precision/recall */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ndd-confusion-section">
        <div className="rounded border border-gray-800 bg-gray-900/60 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">Confusion Matrix</div>
          <div className="grid grid-cols-3 gap-1 text-center text-xs">
            <div></div>
            <div className="text-gray-500 text-[10px]">Actual UP</div>
            <div className="text-gray-500 text-[10px]">Actual DOWN</div>
            <div className="text-gray-500 text-[10px] text-right pr-1">Predicted UP</div>
            <div className="bg-green-900/40 text-green-300 font-mono py-1 rounded">{bt.confusion?.tp ?? 0}</div>
            <div className="bg-red-950/40 text-red-300 font-mono py-1 rounded">{bt.confusion?.fp ?? 0}</div>
            <div className="text-gray-500 text-[10px] text-right pr-1">Predicted DOWN</div>
            <div className="bg-amber-950/40 text-amber-300 font-mono py-1 rounded">{bt.confusion?.fn ?? 0}</div>
            <div className="bg-green-900/40 text-green-300 font-mono py-1 rounded">{bt.confusion?.tn ?? 0}</div>
          </div>
        </div>
        <div className="rounded border border-gray-800 bg-gray-900/60 p-3 grid grid-cols-2 gap-2">
          <SmallMetric label="Precision UP" value={pct(bt.precision_up)} />
          <SmallMetric label="Recall UP" value={pct(bt.recall_up)} />
          <SmallMetric label="Precision DOWN" value={pct(bt.precision_down)} />
          <SmallMetric label="Recall DOWN" value={pct(bt.recall_down)} />
        </div>
      </div>

      {/* Recent prediction history */}
      {history.length > 0 && (
        <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid="ndd-history-chart">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
              Last {history.length} Predictions vs Actual
            </div>
            <div className="text-[10px] text-gray-500 font-mono">
              {history.filter((h) => h.correct).length}/{history.length} correct
            </div>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={history} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={9} interval={Math.max(0, Math.floor(history.length / 8))} />
              <YAxis stroke="#9ca3af" fontSize={9} domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
              <Tooltip content={<HistTip />} />
              <ReferenceLine y={0.5} stroke="#6b7280" strokeDasharray="3 3" />
              <Bar dataKey="prob_up">
                {history.map((h, i) => (
                  <Cell key={i} fill={h.correct ? "#22c55e" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-3 text-[10px] text-gray-500 font-mono">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-sm" /> correct</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm" /> wrong</span>
            <span>Bar height = predicted probability of UP</span>
          </div>
        </div>
      )}

      {/* Feature importance */}
      {featureImportance.length > 0 && (
        <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid="ndd-feature-importance">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
            Top Features (permutation importance, last 500 rows)
          </div>
          <ResponsiveContainer width="100%" height={Math.max(200, featureImportance.length * 22)}>
            <BarChart data={featureImportance} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
              <XAxis type="number" stroke="#9ca3af" fontSize={9} />
              <YAxis type="category" dataKey="feature" stroke="#9ca3af" fontSize={10} width={95} />
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid #374151", fontSize: 10 }}
                formatter={(v) => Number(v).toFixed(4)}
              />
              <Bar dataKey="importance" fill="#06b6d4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Methodology footer */}
      <details className="rounded border border-gray-800 bg-gray-900/40 px-3 py-2 text-[11px] text-gray-400">
        <summary className="cursor-pointer text-gray-300 font-semibold">Methodology</summary>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li><strong>Label:</strong> y_next_up = 1 if close[t+1] {'>'} close[t] else 0</li>
          <li><strong>Validation:</strong> expanding-window walk-forward, refit every 21 trading days, no random shuffle</li>
          <li><strong>Anti-leakage:</strong> features at row t use only data through close[t]; final row dropped (no label)</li>
          <li><strong>Features:</strong> {data.features_used?.length} total — returns, gap, intraday, volatility (RV), SMAs, EMAs, MACD, RSI, ATR, Bollinger z-score, calendar effects{data.context_used?.spy ? ", SPY context" : ""}{data.context_used?.vix ? ", VIX context" : ""}</li>
          <li><strong>Model:</strong> {data.model} (lr 0.05, 200 trees, depth 4, leaf min 20, L2 0.5)</li>
          <li><strong>Confidence interval:</strong> Wilson score 95%</li>
          <li className="text-amber-300/80">Markets are noisy — accuracy near 50% is the norm. Use as one signal among many, not as a standalone strategy.</li>
        </ul>
      </details>
    </div>
  );
}

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function windowLabel(w) {
  const map = {
    "2d": "2-Day", "10d": "10-Day",
    "1mo": "1-Month", "3mo": "3-Month", "6mo": "6-Month",
    "1y": "1-Year", "2y": "2-Year", "3y": "3-Year",
    "5y": "5-Year", "7y": "7-Year", "10y": "10-Year",
  };
  return map[w] || w;
}

function Stat({ label, value, sub, icon, color = "text-white", highlight, testid }) {
  return (
    <div className={`rounded border ${highlight ? "border-cyan-700/60 bg-cyan-950/20" : "border-gray-800 bg-gray-900/60"} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {icon}{label}
      </div>
      <div className={`mt-1 font-mono text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SmallMetric({ label, value }) {
  return (
    <div className="text-center bg-gray-950/60 border border-gray-800 rounded p-2">
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className="font-mono text-sm text-gray-200 font-semibold">{value}</div>
    </div>
  );
}

function HistTip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-[11px] font-mono">
      <div className="text-gray-400">{p.date}</div>
      <div className="text-white">Predicted: <span className={p.predicted === "UP" ? "text-green-300" : "text-red-300"}>{p.predicted}</span> @ {(p.prob_up * 100).toFixed(1)}%</div>
      <div className="text-white">Actual: <span className={p.actual === "UP" ? "text-green-300" : "text-red-300"}>{p.actual}</span></div>
      <div className={p.correct ? "text-green-400" : "text-red-400"}>{p.correct ? "✓ Correct" : "✗ Wrong"}</div>
    </div>
  );
}
